'use client'

import {useTranslations} from 'next-intl'
import {Button, Card} from '@/design-system'
import type {AuthErrorReason} from '@/lib/auth-errors'

/**
 * The banner a failed Google sign-in returns to (T-WEB-09; FR-1 "never a
 * dead end").
 *
 * `role="alert"` so it is announced the moment the page loads — the visitor
 * arrived here by being bounced back from Google and needs to know why. The
 * "Try again" link is a real focusable control immediately after the skip
 * link, so a keyboard user reaches the recovery path in two tabs.
 */
export function AuthErrorBanner({
  reason,
  authHref
}: {
  reason: AuthErrorReason
  authHref: string
}) {
  const t = useTranslations('auth')

  return (
    <div style={{maxWidth: 1120, margin: '0 auto', padding: '16px 20px 0'}}>
      <Card
        role="alert"
        data-testid="auth-error"
        style={{
          background: 'var(--danger-bg)',
          borderColor: 'var(--danger)',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 12,
          alignItems: 'center'
        }}
      >
        <div style={{flex: '1 1 260px', minWidth: 0}}>
          <p
            style={{
              margin: 0,
              fontWeight: 'var(--weight-semibold)',
              color: 'var(--danger)'
            }}
          >
            {t('errorTitle')}
          </p>
          <p style={{margin: '4px 0 0', color: 'var(--ink-2)'}}>{t(`errors.${reason}`)}</p>
        </div>
        <Button href={authHref} size="sm">
          {t('continueWithGoogle')}
        </Button>
      </Card>
    </div>
  )
}
